import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fetchTenants, fetchProviders, fetchAllFunds } from '../../services/adminApi';

const TYPE_LABELS = {
    provider: 'Providers',
    corporate: 'Corporates',
    wealth_manager: 'Wealth Managers',
    waive: 'Waive',
    d2c: 'Direct (D2C)',
};

const TYPE_COLORS = {
    provider: '#4da6ff',
    corporate: '#10b981',
    wealth_manager: '#f59e0b',
    waive: '#8b5cf6',
    d2c: '#d946ef',
};

function StatCard({ icon, label, value, color, sub }) {
    return (
        <Card className="h-100 border-0 shadow-sm">
            <Card.Body className="d-flex align-items-center gap-3 p-4">
                <div style={{
                    width: 48, height: 48, borderRadius: 12,
                    background: color + '20',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                }}>
                    <FontAwesomeIcon icon={icon} style={{ color, fontSize: 20 }} />
                </div>
                <div>
                    <div style={{ fontSize: 28, fontWeight: 700, lineHeight: 1, color: '#0d2137' }}>
                        {value}
                    </div>
                    <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>{label}</div>
                    {sub && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{sub}</div>}
                </div>
            </Card.Body>
        </Card>
    );
}

function Dashboard() {
    const [loading, setLoading] = useState(true);
    const [tenants, setTenants] = useState([]);
    const [providers, setProviders] = useState([]);
    const [fundCount, setFundCount] = useState(0);

    useEffect(() => {
        Promise.all([fetchTenants(), fetchProviders(), fetchAllFunds()])
            .then(([t, p, f]) => {
                setTenants(t.tenants || []);
                setProviders(p.providers || []);
                setFundCount(f.length);
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    if (loading) {
        return (
            <div className="page-content d-flex align-items-center justify-content-center" style={{ minHeight: 300 }}>
                <Spinner animation="border" variant="primary" />
            </div>
        );
    }

    const activeTenants = tenants.filter(t => t.active);
    const byType = Object.entries(TYPE_LABELS).map(([type, label]) => ({
        type, label,
        count: activeTenants.filter(t => t.tenant_type === type).length,
    })).filter(x => x.count > 0);

    return (
        <div className="page-content">
            <h1 className="page-title">Dashboard</h1>
            <p className="text-muted mb-4">Platform overview</p>

            <Row className="g-3 mb-4">
                <Col md={3}>
                    <StatCard icon="building" label="Active Tenants" value={activeTenants.length} color="#4da6ff" />
                </Col>
                <Col md={3}>
                    <StatCard icon="sitemap" label="Fund Providers" value={providers.filter(p => p.active).length}
                        color="#10b981" sub="in provider catalogue" />
                </Col>
                <Col md={3}>
                    <StatCard icon="wallet" label="Funds" value={fundCount} color="#f59e0b" sub="in global catalogue" />
                </Col>
                <Col md={3}>
                    <StatCard icon="users" label="All Tenants" value={tenants.length} color="#8b5cf6"
                        sub={`${tenants.filter(t => !t.active).length} inactive`} />
                </Col>
            </Row>

            <Row className="g-3">
                <Col md={6}>
                    <Card className="border-0 shadow-sm">
                        <Card.Header className="bg-white border-bottom fw-semibold" style={{ fontSize: 14 }}>
                            Tenants by Type
                        </Card.Header>
                        <Card.Body className="p-0">
                            <table className="table table-sm mb-0">
                                <tbody>
                                {byType.map(({ type, label, count }) => (
                                    <tr key={type}>
                                        <td className="px-4 py-3">
                                            <span className="badge rounded-pill" style={{
                                                background: TYPE_COLORS[type] + '20',
                                                color: TYPE_COLORS[type],
                                                fontWeight: 600, fontSize: 12,
                                            }}>
                                                {label}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-end fw-semibold">{count}</td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </Card.Body>
                    </Card>
                </Col>

                <Col md={6}>
                    <Card className="border-0 shadow-sm">
                        <Card.Header className="bg-white border-bottom fw-semibold" style={{ fontSize: 14 }}>
                            Fund Providers
                        </Card.Header>
                        <Card.Body className="p-0">
                            <table className="table table-sm mb-0">
                                <thead>
                                    <tr className="table-light">
                                        <th className="px-4 fw-medium" style={{ fontSize: 12, color: '#64748b' }}>Provider</th>
                                        <th className="px-4 fw-medium text-end" style={{ fontSize: 12, color: '#64748b' }}>Funds</th>
                                        <th className="px-4 fw-medium" style={{ fontSize: 12, color: '#64748b' }}>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                {providers.map(p => (
                                    <tr key={p.id}>
                                        <td className="px-4 py-2">
                                            <span className="fw-semibold" style={{ fontSize: 13 }}>{p.code}</span>
                                            <span className="text-muted ms-2" style={{ fontSize: 12 }}>{p.name}</span>
                                        </td>
                                        <td className="px-4 py-2 text-end" style={{ fontSize: 13 }}>{p.fund_count}</td>
                                        <td className="px-4 py-2">
                                            <span className={`badge ${p.active ? 'bg-success' : 'bg-secondary'}`} style={{ fontSize: 11 }}>
                                                {p.active ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </div>
    );
}

export default Dashboard;
